import { Injectable } from '@angular/core';
import {
  CountyListResponse,
  CountyListService,
  County
} from '@ptl/global-components';

import { NormalizedResponse } from '@ptl/xhr';
import { StateStructure, resetStore } from '@ptl/state';
import { NgRedux } from '@ptl/angular-redux-store';
import { ReducerUtilityService } from './reducer-utility.service';
import { saveZipCounty } from 'src/app/angular-elements/demographics/zip-county-modal/reducers/zipCounty.reducer';
import { zipChangedResetPlans } from 'src/app/angular-elements/plan-selection/configs/reducers/plans.reducer';
import { HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { saveCrossWalkInfo, WHOLE_HEALTH_BALANCE_CROSSWALK_STATE_NAME } from 'src/app/angular-elements/shared/components/configs/reducers/whb-crosswalk.reducer';
import { WholeHealthBalanceCrosswalk } from 'src/app/angular-elements/shared/components/configs/interfaces/whole-health-balance-and-agent-cube-state-structure.interface';
import { saveMemberID, saveUrlParams } from 'src/app/angular-elements/shared/components/configs/reducers/globalData.reducer';
import { URLParams } from 'src/app/angular-elements/shared/components/interfaces/url-params.interface';
import { ProducerStateStructure } from 'src/app/angular-elements/producer/configs/interfaces/producerStateStructure.interface';
import { persistProducer } from 'src/app/angular-elements/producer/configs/reducers/producer.reducer';
import { PrepopulatedFields } from 'src/app/angular-elements/shared/components/interfaces/prepopulated-fields.interface';
import { ROLE_FORM_TYPE } from 'src/app/shared/enums/role-form-type.enum';
import { USER_ROLE, LoggedInUserInformationService, UserInfoStateStructure, LOGIN_INFO_REDUCER_NAME } from '@ptl/account-management';
import { setUserInfo, setUserId, setBasicUserInfo, setMemberInfo } from '@ptl/account-management';
import { ZipCountyStateStructure } from 'src/app/angular-elements/shared/interfaces/zipCountyStateStructure.interface';
import { HTTPWrapperContext, RequestConfigImplementation, REQUEST_METHODS, XHR_STRATEGIES } from '@ptl/xhr';
import { DateService } from '../services/date.service';
import { BrandingConfigurationService } from '@ptl/brand';
import { EffectiveDateStateStructure } from '../../angular-elements/shared/components/configs/interfaces/effective-date.state-structure.interface';
import { setDirtyEffectiveDate, setEffectiveDate } from '../../angular-elements/shared/components/configs/reducers/effective-date.reducer';
import { PrePopulatedDataStateStructure } from '../../angular-elements/shared/components/configs/interfaces/prePopulatedDataStateStructure.interface';
import { savePrepopulatedData } from '../../angular-elements/shared/components/configs/reducers/prePopulatedData.reducer';
import { GlobalDataStateStructure } from '../../angular-elements/shared/components/configs/interfaces/globalStateStructure.interface';
import { ShoppingDateStateStructure } from '../../angular-elements/shared/components/configs/interfaces/shopping-date.state-structure.interface';
import { EnrollmentAppStateStructure } from 'src/app/angular-elements/shared/interfaces/enrollmentAppStateStructure.interface';
import { INITIAL_STATE, persistEnrollmentApp, saveApplicationSubmissionInfo } from '../reducers/enrollmentApp.reducer';
import { resetApplicationQualification } from '../reducers/applicationQualification.reducer'
import { PlanYearOption } from '../../angular-elements/producer/interfaces/application-record.interface';
import { IndividualMarketStateStructure } from 'src/app/angular-elements/form-wrappers/plan-enrollment/individual-plan-enrollment-form/configs/interfaces/individualMarket.interfaceStateStructure';
import { saveIndividualState } from 'src/app/angular-elements/form-wrappers/plan-enrollment/individual-plan-enrollment-form/configs/reducers/individual-market.reducer';
import { SHOPPING_DATE_STATE_NAME } from '../../angular-elements/shared/components/configs/reducers/shopping-dates.reducer';
@Injectable({
  providedIn: 'root'
})
export class CommonUtilityService {

  counties: County[];
  prepopulatedFields: PrepopulatedFields;

  // A soft cache to avoid repeated API calls for the same zip code. Empties on page nav or reload
  countyResponseSoftCache: {[zipCodeAndSegment:string]: NormalizedResponse<CountyListResponse>} = {};

  constructor(
    private ngRedux: NgRedux<StateStructure>,
    private brandService: BrandingConfigurationService,
    private countyListService: CountyListService,
    private reducerUtilityService: ReducerUtilityService,
    private loggedInUserService: LoggedInUserInformationService,
    private httpClient: HTTPWrapperContext,
    private dateUtility: DateService,
  ) { }

  /** Saves data to State and Brand */
  saveCountyZipData(county: County, zipCode: string) {
    if (county?.brand) {
      this.brandService.setBrandCookie(county.brand);
    }
    
    /**
     * This need to dispatch brand to the brand configuration reducer from library
     * - enhance library to handle, within reducer, not just UBK but also this cookieBrandCode
     */

    /** Save the zip county information */
    this.ngRedux.dispatch(saveZipCounty(zipCode, county));

    /** Clear out medigap preauthorization questions to reset rates for new zip */
    this.ngRedux.dispatch(resetApplicationQualification());

    /** Reset Plans, but save Medigap form data */
    this.ngRedux.dispatch(zipChangedResetPlans());
  }

  /**
   * 
   * @param zipCode - 5-digit zip corresponding to the relevant ASP
   * @param segment - I don't see it used anywhere app-member-lookup is referenced? COUNTY_LIST_SEGMENT
   * @returns the HTTP response of the countyList service - does not set brand
   */
  async fetchCounty(zipCode: string, segment?: string): Promise<NormalizedResponse<CountyListResponse>> {
    if (this.countyResponseSoftCache[zipCode + segment]) {
      return this.countyResponseSoftCache[zipCode + segment];
    }

    try {
      this.countyResponseSoftCache[zipCode + segment] = await this.countyListService.getCountyInformation(zipCode, segment);
      return this.countyResponseSoftCache[zipCode + segment];
    } 
    catch (e) {
      const error: HttpErrorResponse = e as HttpErrorResponse;
      if (e?.payload == null) {
        return {} as NormalizedResponse<CountyListResponse>;
      }

      return e;
    }
  }

  async setBrandByZipCode(zipCode: string, countyPreselect?: string, segment?: string): Promise<boolean> {
    try {
      const countyResponse: NormalizedResponse<CountyListResponse> = await this.fetchCounty(zipCode, segment);
      this.counties = countyResponse.payload.countyList;

      let countyIndex = 0;
      if (countyPreselect) {
        for (let i = 0; i < this.counties.length; i++) {
          if (countyPreselect.toUpperCase() === this.counties[i].county.toUpperCase()) {
            countyIndex = i;
          }
        }
      }

      this.saveCountyZipData(this.counties[countyIndex], zipCode);
      return !!countyResponse;

    } catch (e) {
      const error: HttpErrorResponse = e as HttpErrorResponse;
      const normalizedError: NormalizedResponse<any> = error.error;
      if (
        normalizedError &&
        normalizedError.baseMessage &&
        normalizedError.baseMessage.returnCodeDescription === 'GEN-INVINPUT'
      ) {
        return false;
      }
      return false;
    }
  }

  saveQueryParams(data: URLParams) {
    this.ngRedux.dispatch(saveUrlParams(data));
  }

  resetStateForNewApplicationFlow() {
    // Capture additional states to reapply after clearing entire state\
    const individualMarketData: IndividualMarketStateStructure = this.reducerUtilityService.getPayloadStates().individualMarket;
    const globalData: GlobalDataStateStructure = this.reducerUtilityService.getState('GlobalData')
    const prePopulateData: PrePopulatedDataStateStructure = this.reducerUtilityService.getState('PrePopulateData');
    const effectiveState: EffectiveDateStateStructure = this.reducerUtilityService.getReducerFromApplicationState('EffectiveDate');

    // Clear state normally first
    this.resetStateExceptUserData();

    // Effective date gets reset by the year-selection.component.ts if we don't set it to dirty first
    this.ngRedux.dispatch(setDirtyEffectiveDate());
    this.ngRedux.dispatch(setEffectiveDate(effectiveState.effectiveDate));

    // Reapply prepop data only if our customer and opportunity ids match the most recent global params
    
    const isValidAgentCubedData = (
      globalData?.urlParams?.opportunityId &&
      globalData.urlParams.customerId &&
      ( globalData.urlParams.opportunityId === prePopulateData?.prepopulatedFields?.opportunityId ) &&
      ( globalData?.urlParams?.customerId === prePopulateData?.prepopulatedFields?.customerId )
    );
    
    const isValidSalesForceData = (
      globalData?.urlParams?.crmOpportunityId &&
      ( globalData.urlParams.crmOpportunityId === prePopulateData?.prepopulatedFields?.salesforceOpportunityId )
    );

    const isValidMember = globalData?.findMemberId


    if (isValidAgentCubedData || isValidSalesForceData || isValidMember) {
      this.ngRedux.dispatch(savePrepopulatedData(prePopulateData.prepopulatedFields));
      if(isValidMember){
        this.ngRedux.dispatch(saveIndividualState(individualMarketData));      
      }
    }
    // New apps also need to clear the enrollment data in case an old app ID is stored from saving a different app
    this.ngRedux.dispatch(saveApplicationSubmissionInfo({} as EnrollmentAppStateStructure));
    this.ngRedux.dispatch(saveMemberID(isValidMember))
  }

  resetStateExceptUserData() {
    const userData: UserInfoStateStructure = this.reducerUtilityService.getReducerFromApplicationState(LOGIN_INFO_REDUCER_NAME);
    const whbAndAgentCubeInfo: WholeHealthBalanceCrosswalk = this.reducerUtilityService.getState(WHOLE_HEALTH_BALANCE_CROSSWALK_STATE_NAME);
    const producerInfo: ProducerStateStructure = this.reducerUtilityService.getState('Producer');
    const zipState: ZipCountyStateStructure = this.reducerUtilityService.getState('ZipCounty');
    const enrollState: EnrollmentAppStateStructure = this.reducerUtilityService.getState('EnrollmentApp');
    enrollState.appId = INITIAL_STATE.appId;

    this.ngRedux.dispatch(resetStore());
    this.ngRedux.dispatch(saveCrossWalkInfo(whbAndAgentCubeInfo.whbcrosswalk));
    this.ngRedux.dispatch(setUserId(userData.userId));
    this.ngRedux.dispatch(setUserInfo(userData.userInfo));
    this.ngRedux.dispatch(setBasicUserInfo(userData.basicUserInfo));
    this.ngRedux.dispatch(setMemberInfo(userData.memberInfo));
    this.ngRedux.dispatch(persistProducer(producerInfo));
    this.ngRedux.dispatch(persistEnrollmentApp(enrollState));
    this.ngRedux.dispatch(saveZipCounty(zipState.zipCode, zipState.county));
  }

  /**
   * Default the enrollment type to consumer, then, based on response from user login, determine if
   * we should modify it to be a TELESALES (agent) type, or, so long as the user is not
   * identified as a visitor or member, use the  PRODUCER type
   */
  getEnrollmentFormType(): ROLE_FORM_TYPE {
    const userData = this.loggedInUserService.getUserData();

    if (userData?.basicUserInfo?.userType) {
      const userTypeAsEnum: USER_ROLE = userData.basicUserInfo.userType.toLowerCase() as USER_ROLE;

      if (userTypeAsEnum === USER_ROLE.MEMBER || userTypeAsEnum === USER_ROLE.VISITOR) {
        return ROLE_FORM_TYPE.CONSUMER
      }

      if (userTypeAsEnum === USER_ROLE.INTERNAL || userTypeAsEnum === USER_ROLE.PLAN) {
        return ROLE_FORM_TYPE.PRODUCER
      }

      if (userTypeAsEnum === USER_ROLE.AGENCY_ADMIN || userTypeAsEnum === USER_ROLE.AGENT) {
        const producerInfo: ProducerStateStructure = this.reducerUtilityService.getState('Producer');

        if (this.loggedInUserService.isMedicareFieldAgent() && producerInfo?.otherUserInfo?.isOnCallWithFieldAgent) {
          return ROLE_FORM_TYPE.TELESALES;
        } else if (this.loggedInUserService.isMedicareTelesales()) {
          return ROLE_FORM_TYPE.TELESALES;
        } else {
          return ROLE_FORM_TYPE.PRODUCER;
        }
      }
    }

    return ROLE_FORM_TYPE.CONSUMER;
  }


  isConsumer(loginInfo?): boolean {
    if (!loginInfo) {
      loginInfo = this.reducerUtilityService.getReducerFromApplicationState(LOGIN_INFO_REDUCER_NAME);
    }

    if (!loginInfo || !loginInfo.basicUserInfo || !loginInfo.basicUserInfo.userType ||
        loginInfo.basicUserInfo.userType.toLowerCase() === USER_ROLE.MEMBER ||
        loginInfo.basicUserInfo.userType.toLowerCase() === USER_ROLE.VISITOR) {
      return true;
    }

    return false;
  }

  isConsumerDomain(): boolean {
    if (window.location.hostname.indexOf('producer') === -1) {
      return true;
    }

    return false;
  }

  // TODO: This needs cleaned up because it's confusing and may be using inaccurate entitlements
  isFieldAgentAsProducer(): boolean {
    const producerInfo: ProducerStateStructure = this.reducerUtilityService.getState('Producer');
    // If field agent is not on call with user then act as producer and not telesales
    if (this.loggedInUserService.isMedicareFieldAgent() && !producerInfo.otherUserInfo.isOnCallWithFieldAgent) {
      return true;
    }
    return false;
  }
  /**
   * Returns user is agent if user type is either agent or agency admin or internal user
   */
  isAgent(loginInfo?: UserInfoStateStructure): boolean {

    if (!loginInfo) {
      loginInfo = this.reducerUtilityService.getReducerFromApplicationState(LOGIN_INFO_REDUCER_NAME);
    }

    if (loginInfo?.basicUserInfo?.userType
      && (loginInfo.basicUserInfo.userType.toLowerCase() === USER_ROLE.AGENT
        || loginInfo.basicUserInfo.userType.toLowerCase() === USER_ROLE.AGENCY_ADMIN
        || loginInfo.basicUserInfo.userType.toLowerCase() === USER_ROLE.INTERNAL)
    ) {
      return true;
    }
    return false;
  }

  cookieExists(cookieName: string) {
    const regExp = new RegExp('^(?:.*;)?\\s*' + cookieName + '\\s*=\\s*([^;]+)(?:.*)?$');
    return document.cookie.match(regExp);
  }


  registerReferralClick(refId: string): Promise<string> {
    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      url: '/sxesvc/api/v1/referralUpdate/status',
      headers: new HttpHeaders({
      }),
      POST_OR_PUT_PAYLOAD: {
        referralId: refId
      },
      responseType: 'text'
    });
    this.httpClient.disableErrorSnackbar(requestConfig);
    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_PRE_PASS);
    return this.httpClient.makeRequest(requestConfig);
  }

  base64encode(str: string): string {
    if (str) {
      return this.bytesToBase64(new TextEncoder().encode(str));
    }
    return '';
  }

  base64decode(str: string): string {
    if (str) {
      return new TextDecoder().decode(this.base64ToBytes(str));
    }
    return '';
  }

  // https://developer.mozilla.org/en-US/docs/Glossary/Base64#the_unicode_problem
  private base64ToBytes(base64) {
    const binString = atob(base64);
    return Uint8Array.from(binString, (m) => m.codePointAt(0));
  }
  
  private bytesToBase64(bytes) {
    const binString = String.fromCodePoint(...bytes);
    return btoa(binString);
  }

  annualEnrollmentPeriod(): boolean {
    const receivedDates = this.dateUtility.getTodayAsFormattedString('MM/DD/YYYY');
    const dateRange = new Date(receivedDates);
    const todayDate = dateRange.getDate();
    const month = dateRange.getMonth() + 1;
    if ((month === 10 && todayDate >= 15) || (month === 11 && todayDate >= 1) || (month === 12 && todayDate <= 7)) {
      return true;

    }
    return false;
  }

  setNestedProperty(obj: {}, dsv: string, changePropertyValueTo: string) {

    const path: string[] = dsv.split('.');

    for (let i = 0; i < path.length; i++) {
      if (obj && obj.hasOwnProperty(path[i])) {
        obj = obj[path[i]];

      } else {
        console.warn(`${path[i]} does not exist on `, obj);
      }


    }
    return obj;
  }

  getYearOptionsForAgentPermissions(): PlanYearOption[] {
      const shoppingDatesState: ShoppingDateStateStructure = this.reducerUtilityService.getApplicationStates()[SHOPPING_DATE_STATE_NAME];
  
      // Create a copy of the shopping dates, except normalize all the dates to reflect the current year so only month and day are important
      const customShoppingDates = {
        sellThisYearProductsDate: this.dateUtility.getDateAsCurrentYearDate(shoppingDatesState.sellThisYearProductsDate),
        showNextYearProductsDate: this.dateUtility.getDateAsCurrentYearDate(shoppingDatesState.showNextYearProductsDate),
        sellNextYearProductsDate: this.dateUtility.getDateAsCurrentYearDate(shoppingDatesState.sellNextYearProductsDate)
      };

      return this.getYearOptionsForShoppingDates(customShoppingDates);
  }

  /**
   * @description returns an array of available year options based off of the shopping dates passed in.
   * 
   * @param shoppingDates a shopping date structure container the specific dates to compare against today
   * 
   * @returns An array of strings with all possible year values
   */
  getYearOptionsForShoppingDates(shoppingDates: ShoppingDateStateStructure): PlanYearOption[] {
    let yearOptions = [];

    let currentYear = this.dateUtility.getTodayAsMoment().year();
    if (this.shouldShowThisYearsPlans(shoppingDates.sellThisYearProductsDate)) {
      yearOptions.push({ value: currentYear.toString(), label: currentYear.toString() });
    }

    if (this.shouldShowNextYearsPlans(shoppingDates)) {
      let nextYear = currentYear + 1;
      yearOptions.push({ value: nextYear.toString(), label: nextYear.toString() });
    }
    
    return yearOptions;
  }

  /** @description If both this year and next year are to be included in plan shopping, we should show the year selector */
  shouldShowYearSelectorByDates(shoppingDates: ShoppingDateStateStructure): boolean {
    return this.shouldShowThisYearsPlans(shoppingDates.sellThisYearProductsDate) && this.shouldShowNextYearsPlans(shoppingDates);
  }

  shouldShowThisYearsPlans(sellThisYearProductsDate: string): boolean {
    const nextMonthsYear = this.dateUtility.getFirstOfNextMonth().year();
    const thisYear = this.dateUtility.getTodayAsMoment().year();
    const nextMonthsYearIsThisYear = nextMonthsYear === thisYear;

    // sellThisYearProductsDate allows us to sell this year's products until this date. December 1st is the default cutoff
    let shouldSellThisYearProductsDate = false;
    if (sellThisYearProductsDate && this.dateUtility.getTodayAsMoment().isSameOrBefore(sellThisYearProductsDate)) {
      shouldSellThisYearProductsDate = true;
    }
    else {
      // As long as next month is in the same year as this month, we continue to sell this years products
      shouldSellThisYearProductsDate = nextMonthsYearIsThisYear;
    }

    return shouldSellThisYearProductsDate;
  }

  shouldShowNextYearsPlans(shoppingDates: ShoppingDateStateStructure): boolean {
    const shouldSellNextYearProductsDate = this.dateUtility.getTodayAsMoment().isSameOrAfter(shoppingDates.sellNextYearProductsDate);
    const isWindowShopping = this.dateUtility.getTodayAsMoment().isSameOrAfter(shoppingDates.showNextYearProductsDate)
      && this.dateUtility.getTodayAsMoment().isBefore(shoppingDates.sellNextYearProductsDate);

    return shouldSellNextYearProductsDate || isWindowShopping;
  }

  // The arg 'pages' should be an array of regex strings, but still 
  // combined into one singular string that is passed directly from AEM.
  // Ex: pages = '["/test1", "/test2"]'
  pageMatched(pages: string): boolean {
    const config = JSON.parse(pages);
    if (config.length > 0) {
      const regex = config.join('|').replace(/\|?\[object Object\]|\|?{}/ig, ''); // Cleans up accidentally empty fields
      if (regex.length > 0) {
        const pageMatched = RegExp(regex).exec(window.location.href);
        return !!pageMatched;
      }
    }

    return false;
  }

  /**
   * Returns the test environment suffix derived from the hostname
   * (e.g. 'shopenv3.highmark.com' -> 'tenv3'). Returns '' when not in a test environment.
   */
  public getTestEnvironment(hostname: string = window.location.hostname): string {
    const envIndex = hostname.indexOf('tenv');
    if (envIndex >= 0) {
      return hostname.substring(envIndex, hostname.indexOf('.'));
    }
    return '';
  }
}
